/**
 * Portfolio endpoints: portfolios, the holdings summary / accounts view,
 * daily change, allocation, export.
 */

import type {
  Account,
  ExportedPortfolioData,
  HoldingsSummaryResponse,
  Portfolio,
  PortfolioAllocationResponse,
} from '@luminaria/shared';
import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

export type { Portfolio };

/**
 * Account with pre-joined holdings and a transaction count
 * (GET /portfolios/:id/accounts-view).
 * The account part is the same serialized account `GET /portfolios/:id/accounts`
 * returns, so this one query is the client's only accounts cache (WEBD-24):
 * `useAccounts` selects plain `Account`s from it with `accountFromView`.
 */
export interface AccountView extends Omit<Account, 'holdings' | 'transactions' | 'balance' | 'currentValue'> {
  balance?: number | null;
  currentValue?: number | null;
  /** When the broker last delivered data to SnapTrade (can lag lastSyncAt by days). */
  brokerDataAsOf?: string | null;
  /** Brokerage slug of a connected account's authorization. */
  snaptradeBrokerageId?: string | null;
  totalValue: number;
  holdingsCount: number;
  holdings: Array<{
    id: string;
    accountId: string;
    symbol: string | null;
    quantity: number;
    price: number | null;
    value: number | null;
    costBasis: number;
    [key: string]: unknown;
  }>;
  /** Number of transactions in the account; the rows are not embedded (LAT-02). */
  transactionCount: number;
}

/** The plain account of an accounts-view row (joined rows and view totals dropped). */
export function accountFromView(view: AccountView): Account {
  const { holdings: _holdings, transactionCount: _transactionCount, totalValue: _totalValue, holdingsCount: _holdingsCount, balance, currentValue, ...account } = view;
  return { ...account, balance: balance ?? undefined, currentValue: currentValue ?? undefined };
}

export interface QuoteData {
  symbol: string;
  price: number;
  change: number | null;
  changePercent: number | null;
  name?: string;
  previousClose?: number;
  timestamp?: number;
  marketState?: string;
}

export interface HoldingEnrichment {
  holdingId: string;
  symbol: string;
  quotePrice: number;
  quoteChange: number | null;
  quoteChangePercent: number | null;
  marketValue: number;
  dayChange: number | null;
  positionDirection: 'long' | 'short';
  openingCredit?: number; // For short options: premium received from SellToOpen
}

export interface AccountDailyChange {
  accountId: string;
  accountName: string;
  totalDailyChange: number;
  totalDailyChangePercent: number;
  totalMarketValue: number;
}

export interface PortfolioDailyChange {
  totalDailyChange: number;
  totalDailyChangePercent: number;
  totalMarketValue: number;
  byAccount: AccountDailyChange[];
  quotes: Record<string, QuoteData>;
  /** Per-holding enrichment data with pre-calculated market values and day changes */
  holdingEnrichments: HoldingEnrichment[];
}

/** `accountIds` → the comma-joined query param the portfolio routes accept (omitted when empty). */
export function accountIdsParam(accountIds?: readonly string[]): { accountIds?: string } {
  return accountIds && accountIds.length > 0 ? { accountIds: accountIds.join(',') } : {};
}

export const portfoliosApi = {
  async list(): Promise<Portfolio[]> {
    const { data } = await apiClient.get<{ portfolios: Portfolio[] }>(API_ENDPOINTS.PORTFOLIOS.LIST);
    return data.portfolios ?? [];
  },

  /** Deletes every portfolio of the current user. Resolves the deleted count. */
  async removeAll(): Promise<number> {
    const { data } = await apiClient.delete<{ deletedCount: number }>(API_ENDPOINTS.PORTFOLIOS.LIST);
    return data.deletedCount;
  },

  async holdingsSummary(portfolioId: string, accountIds?: readonly string[]): Promise<HoldingsSummaryResponse> {
    const { data } = await apiClient.get<HoldingsSummaryResponse>(API_ENDPOINTS.PORTFOLIOS.HOLDINGS_SUMMARY(portfolioId), {
      params: accountIdsParam(accountIds),
    });
    return data;
  },

  async accountsView(portfolioId: string): Promise<AccountView[]> {
    const { data } = await apiClient.get<{ accounts: AccountView[] }>(API_ENDPOINTS.PORTFOLIOS.ACCOUNTS_VIEW(portfolioId));
    return data.accounts ?? [];
  },

  async dailyChange(portfolioId: string): Promise<PortfolioDailyChange> {
    const { data } = await apiClient.get<PortfolioDailyChange>(API_ENDPOINTS.PORTFOLIOS.DAILY_CHANGE(portfolioId));
    return data;
  },

  async allocation(portfolioId: string, accountIds?: readonly string[]): Promise<PortfolioAllocationResponse> {
    const { data } = await apiClient.get<PortfolioAllocationResponse>(API_ENDPOINTS.PORTFOLIOS.ALLOCATION(portfolioId), {
      params: accountIdsParam(accountIds),
    });
    return data;
  },

  async exportData(portfolioId: string): Promise<ExportedPortfolioData> {
    const { data } = await apiClient.get<{ exportData: ExportedPortfolioData }>(API_ENDPOINTS.PORTFOLIOS.EXPORT(portfolioId));
    return data.exportData;
  },
};
